/**
 * Files Tab Component
 * File management interface for orders.
 * 
 * Features:
 * - File upload with drag & drop
 * - File listing with icons and metadata
 * - Download, Preview, Rename, Delete operations
 * - Send file via email
 * - Role-based permissions
 * 
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 * 
 * Extracted from App.js for better code organization.
 */

import { holdsRole } from '../../utils/heldRoles';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Upload, FileText, Image, Trash2, Download, MoreVertical,
  Eye, Edit, Send, CheckCircle, Loader2, X, File, Mail, Plus, Lock,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { SendFileModal, RenameFileModal, DeleteFileModal } from '../modals/FileModals';
import { useAuth } from '../../App';
import { formatDateOnlyET, formatRelativeTimeET } from '../../utils/formatters';
import { openAuthenticatedFile } from '../../utils/filePreview';
import { PostCompletionUploadModal } from './PostCompletionUploadModal';
import './post-completion-upload.css';
import FileUploadModal, { FILE_CATEGORIES, validateFileForCategory } from './FileUploadModal';
import { FileCategorySections, groupFilesByCategory } from './FileCategorySection';
import { isMlsFile } from './fileClassification';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

const FilesTab = ({ orderData }) => {
  // MLS-DOC-CLASS-001: classification UI only for MLS + Tax dual orders
  const isDualMlsOrder = !!(orderData?.components && orderData.components.mls && orderData.components.tax);
  const [classFilter, setClassFilter] = useState('all');
  const [files, setFiles] = useState([]);
  const classifiableFiles = files.filter((f) => f.is_active !== false && !f.is_final_pdf && f.file_category !== 'tax_certificate_package');
  const [fetchError, setFetchError] = useState(null); // FILES-ERR-001: { kind: 'auth' | 'fetch', status, message }
  const [loading, setLoading] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newFilename, setNewFilename] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false); // New upload modal state
  const [uploadModalCategory, setUploadModalCategory] = useState(''); // Pre-selected category for upload modal
  // Jun 2026 — Option C override-reason prompt state. When the backend
  // returns 423 + requires_override_reason on a locked-order
  // upload/rename, we open this modal, await the reason, and retry.
  // `pending` holds a single-use thunk that resolves the awaiting
  // Promise with the entered reason (or null on cancel).
  const [overrideModal, setOverrideModal] = useState({
    open: false,
    title: '',
    description: '',
    pending: null,
  });
  const fileInputRef = useRef(null);
  const taxSearchDocInputRef = useRef(null);

  useEffect(() => {
    fetchFiles();
  }, [orderData.id]);
  
  // Listen for openFilesTab events from Smart Evidence Prompt buttons
  useEffect(() => {
    const handleOpenFilesTab = (event) => {
      const { category } = event.detail || {};
      console.log('[FilesTab] Received openFilesTab event with category:', category);
      
      if (category) {
        setUploadModalCategory(category);
        setShowUploadModal(true);
      }
    };
    
    window.addEventListener('openFilesTab', handleOpenFilesTab);
    
    return () => {
      window.removeEventListener('openFilesTab', handleOpenFilesTab);
    };
  }, []);

  // Feb 26, 2026 — Files-tab parity with Fulfillment evidence uploads.
  // <EvidenceDropZone> dispatches `orderFilesChanged` after every batch
  // upload/delete so a mounted Files tab reflects the new list without
  // requiring a manual refresh. Cheap: just refetches the order's files.
  useEffect(() => {
    const handleFilesChanged = () => {
      fetchFiles(true);
    };
    window.addEventListener('orderFilesChanged', handleFilesChanged);
    return () => {
      window.removeEventListener('orderFilesChanged', handleFilesChanged);
    };
  }, [orderData.id]);

  // Check if order is locked and user permissions
  const { user } = useAuth();
  // COMPLETED-ORDER-DOC-UPLOAD: both legacy 'complete' and current 'completed' statuses are locked.
  const isCompleted = ['completed', 'complete'].includes(orderData?.status);
  const isOrderLocked = orderData?.is_locked || isCompleted;
  const isProcessor = (() => {
    const r = (user?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    return ['processor', 'tax_processor', 'title_search_processor', 'search_processor'].includes(r);
  })();
  const isAdmin = user?.role === 'admin';
  const isTeamLeader = user?.role === 'tax_search_team_leader' || user?.role === 'title_search_team_leader' || user?.role === 'team_leader';
  // Accounting opens orders from Billing in strict read-only mode — they can
  // view & download every file but cannot upload, delete, or rename.
  // Backend RBAC already rejects these mutations (see require_role([...]) on
  // POST /files/upload, DELETE /files/{id}, PATCH /files/{id}); this flag drives the UI.
  const isAccounting = user?.role === 'accounting';
  // OD-DUAL-ROLE-001: an Order Desk user holding the additional Search Processor role gets processor file rights
  const isOrderDesk = user?.role === 'order_desk' && !(holdsRole(user, 'search_processor') && orderData?.assignee_id === user?.id); // TITLEFLOW-ORDER-ENTRY-DOCUMENTS-001: view/download + Prior Search Package upload only

  // For locked orders, processors have limited permissions
  // Accounting role gets ZERO mutation rights regardless of order state.
  //
  // Jun 2026 — Option C: file UPLOAD + RENAME on locked orders now go
  // through the override-reason flow. Team Leaders + Admin are
  // override-capable (per backend LOCK_OVERRIDE_ROLES); the backend
  // gates the actual write and emits an audit event when the reason
  // is supplied. Processors / QC still see the static "locked" copy.
  // DELETE stays Admin-only on locked orders by policy (no override path).
  // COMPLETED-ORDER-DOC-UPLOAD: on Completed orders the only upload path is the explicit
  // post-completion "Add Document" (Admin / Title TL / Tax TL, reason required, reference-only).
  const canPostCompletionUpload = isCompleted && !isAccounting
    && (isAdmin || user?.role === 'tax_search_team_leader' || user?.role === 'title_search_team_leader');
  const canUploadFiles = !isAccounting && (isCompleted ? canPostCompletionUpload : (!isOrderLocked || isAdmin || isTeamLeader));
  const canDeleteFiles = !isAccounting && !isOrderDesk && (!isOrderLocked || isAdmin);
  const canRenameFiles = !isAccounting && (!isOrderLocked || isAdmin || isTeamLeader);
  const canSendFiles = !isAccounting; // Accounting cannot send files either
  // Note: canMarkFinal removed - 'Mark as Final' functionality has been removed

  // `silent = true` refreshes the list in the background WITHOUT flipping
  // the loading skeleton — used after delete/upload/rename so the tab
  // doesn't flash/remount (FILES-FLICKER-001, Jun 2026).
  const fetchFiles = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/orders/${orderData.id}/files`);
      setFiles(response.data.files || []);
      setFetchError(null);
    } catch (error) {
      console.error('Error fetching files:', error);
      // FILES-ERR-001: never present a failed request as a genuine "0 files" state
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const detailText = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : detail?.message);
      const kind = status === 401 || status === 403 ? 'auth' : 'fetch';
      setFetchError({
        kind, status,
        message: kind === 'auth'
          ? (detailText || (status === 401 ? 'Your session has expired — please sign in again.' : 'You do not have access to the files for this order.'))
          : (detailText || 'Unable to load files. Please try again.'),
      });
      if (!silent) setFiles([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // DUP-SUBMIT-001 — after an upload error (possibly a lost response) refresh the
  // list FIRST and report which files actually landed instead of inviting a retry.
  const filesAlreadyUploaded = async (filesList) => {
    try {
      const res = await axios.get(`${API}/orders/${orderData.id}/files`);
      const fresh = res.data.files || [];
      setFiles(fresh);
      const cutoff = Date.now() - 5 * 60 * 1000;
      return filesList.filter((f) => fresh.some((x) => x.is_active !== false && x.filename === f.name
        && x.file_size === f.size && new Date(x.uploaded_at || x.created_at).getTime() >= cutoff));
    } catch { return []; }
  };

  const uploadOutcomeToast = (filesList, suppressed, label) => {
    const n = filesList.length, s = n > 1 ? 's' : '';
    if (suppressed >= n) toast.info(`File${s} already uploaded — duplicate suppressed, nothing new was added`, { id: 'dup-upload' });
    else toast.success(`Uploaded ${n - suppressed} file${n - suppressed > 1 ? 's' : ''}${label ? ` to ${label}` : ''}${suppressed ? ` (${suppressed} duplicate skipped)` : ''}`);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Use centralized formatter that properly handles timezone conversion to ET
  // V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
  const getRelativeTime = formatRelativeTimeET;

  const getFileIcon = (fileType, filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    
    if (fileType === 'application/pdf' || extension === 'pdf') {
      return (
        <div className="w-16 h-20 bg-gradient-to-b from-red-50 to-red-100 rounded-lg border border-red-200 flex flex-col items-center justify-center shadow-sm">
          <FileText className="w-8 h-8 text-red-600 mb-1" />
          <span className="text-xs font-medium text-red-700 uppercase">PDF</span>
        </div>
      );
    } else if (fileType?.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
      return (
        <div className="w-16 h-20 bg-gradient-to-b from-green-50 to-green-100 rounded-lg border border-green-200 flex flex-col items-center justify-center shadow-sm">
          <Image className="w-8 h-8 text-green-600 mb-1" />
          <span className="text-xs font-medium text-green-700 uppercase">{extension}</span>
        </div>
      );
    } else if (['doc', 'docx'].includes(extension)) {
      return (
        <div className="w-16 h-20 bg-gradient-to-b from-blue-50 to-blue-100 rounded-lg border border-blue-200 flex flex-col items-center justify-center shadow-sm">
          <FileText className="w-8 h-8 text-blue-600 mb-1" />
          <span className="text-xs font-medium text-blue-700 uppercase">DOC</span>
        </div>
      );
    } else if (['xls', 'xlsx'].includes(extension)) {
      return (
        <div className="w-16 h-20 bg-gradient-to-b from-emerald-50 to-emerald-100 rounded-lg border border-emerald-200 flex flex-col items-center justify-center shadow-sm">
          <FileText className="w-8 h-8 text-emerald-600 mb-1" />
          <span className="text-xs font-medium text-emerald-700 uppercase">XLS</span>
        </div>
      );
    } else {
      return (
        <div className="w-16 h-20 bg-gradient-to-b from-slate-50 to-slate-100 rounded-lg border border-slate-200 flex flex-col items-center justify-center shadow-sm">
          <FileText className="w-8 h-8 text-slate-600 mb-1" />
          <span className="text-xs font-medium text-slate-700 uppercase">FILE</span>
        </div>
      );
    }
  };

  const getStatusRibbon = (file) => {
    if (file.is_final_pdf) {
      return (
        <div className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
          Final PDF
        </div>
      );
    } else if (file.status === 'sent') {
      return (
        <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
          Sent
        </div>
      );
    } else if (file.is_new) {
      return (
        <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
          New
        </div>
      );
    } else if (file.is_signed) {
      return (
        <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
          Signed
        </div>
      );
    }
    return null;
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFileUpload(droppedFiles);
  };

  // New category-based upload handler for the upload modal
  const handleCategoryUpload = async (filesList, category, classification = null, postCompletionNote = '') => {
    if (!filesList || filesList.length === 0) return;
    if (isCompleted && !(canPostCompletionUpload && postCompletionNote.trim().length >= 5)) {
      toast.error('A reason/note is required to add a document to a Completed order.');
      return;
    }
    
    setUploadingFiles(true);
    try {
      // Jun 2026 — Option C: keep override_reason in closure across the
      // batch so a user prompted once on file #1 doesn't get re-prompted
      // for files #2..N in the same upload click. Reason is audited per
      // upload on the backend (each upload = one activity event).
      let overrideReasonForBatch = '';
      let suppressed = 0;
      for (const file of filesList) {
        const doUpload = async () => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', category);
          // MLS-DOC-CLASS-001: explicit Tax / MLS (+section) classification on dual orders
          if (classification?.component) {
            formData.append('component', classification.component);
            if (classification.section) formData.append('section', classification.section);
            if (classification.utility_type) formData.append('utility_type', classification.utility_type);
          }
          if (category === 'tax_search_document') {
            formData.append('is_tax_search_document', 'true');
          }
          if (isCompleted) {
            // COMPLETED-ORDER-DOC-UPLOAD: explicit reference-only attachment (never an edit override)
            formData.append('post_completion', 'true');
            formData.append('post_completion_note', postCompletionNote.trim());
          } else if (overrideReasonForBatch) {
            formData.append('override_reason', overrideReasonForBatch);
          }
          return axios.post(`${API}/orders/${orderData.id}/files/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        };
        try {
          const res = await doUpload();
          if (res?.data?.duplicate_suppressed) suppressed += 1;
        } catch (err) {
          if (is423OverrideRequired(err) && (isAdmin || isTeamLeader)) {
            const reason = await promptForOverrideReason(
              'Order is locked — enter override reason to upload',
              `This order is locked (${err.response.data.detail.order_status}). Override reason will be audited.`,
            );
            if (!reason) {
              toast.info('Upload cancelled');
              return;
            }
            overrideReasonForBatch = reason;
            const res = await doUpload();
            if (res?.data?.duplicate_suppressed) suppressed += 1;
          } else {
            throw err;
          }
        }

        // Log activity for file upload
        const categoryLabel = FILE_CATEGORIES[category]?.label || category;
        await logFileActivity('upload', { 
          filename: file.name, 
          size: file.size,
          category: categoryLabel
        });
      }
      
      uploadOutcomeToast(filesList, suppressed, FILE_CATEGORIES[category]?.label || category);
      setShowUploadModal(false);
      await fetchFiles(true);
      window.dispatchEvent(new CustomEvent('filesChangedInOrder'));
    } catch (error) {
      console.error('Error uploading files:', error);

      // DUP-SUBMIT-001: a lost response looks like an error but the file may have landed.
      const landed = await filesAlreadyUploaded(filesList);
      if (landed.length === filesList.length) {
        toast.info('Upload already recorded — file list refreshed, no retry needed', { id: 'dup-upload' });
        setShowUploadModal(false);
        window.dispatchEvent(new CustomEvent('filesChangedInOrder'));
        return;
      }
      
      // Show specific error message from backend
      let errorMessage = 'Failed to upload files';
      // 423 lock errors return { detail: { message, locked, ... } } — surface the message field.
      if (error.response?.status === 423 && error.response?.data?.detail?.message) {
        errorMessage = error.response.data.detail.message;
      } else if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : (error.response.data.detail.message || 'Failed to upload files');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = `Upload error: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleFileUpload = async (filesList, isTaxSearchDoc = false) => {
    if (isCompleted) {
      toast.info(canPostCompletionUpload ? 'Use “Add Document” to attach a reference document with a reason.' : 'Order is completed and locked.');
      return;
    }
    if (!filesList || filesList.length === 0) return;
    
    // File size validation (15MB max)
    const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB in bytes
    const oversizedFiles = Array.from(filesList).filter(file => file.size > MAX_FILE_SIZE);
    
    if (oversizedFiles.length > 0) {
      toast.error(`File size exceeds 15MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    // File type validation for Tax Search Document
    if (isTaxSearchDoc) {
      const allowedTypes = [
        // Documents
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/rtf',
        'text/rtf',
        // Images
        'image/jpeg',
        'image/png',
        'image/tiff',
        'image/gif',
        // Audio
        'audio/mpeg',
        'audio/mp3',
        'audio/mp4',
        'audio/x-m4a',
        'audio/wav',
        'audio/x-wav',
        'audio/aac',
        'audio/amr'
      ];
      
      const allowedExtensions = [
        '.pdf', '.doc', '.docx', '.nb', '.one', '.rtf',
        '.jpg', '.jpeg', '.png', '.tif', '.tiff', '.gif',
        '.mp3', '.m4a', '.wav', '.aac', '.amr'
      ];
      
      const invalidFiles = Array.from(filesList).filter(file => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        return !allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension);
      });
      
      if (invalidFiles.length > 0) {
        toast.error(`Invalid file type: ${invalidFiles.map(f => f.name).join(', ')}. Please upload PDF, Word, images, or audio files.`);
        return;
      }
    }
    
    setUploadingFiles(true);
    try {
      // Jun 2026 — Option C: share override reason across the batch.
      // See handleCategoryUpload above for the rationale.
      let overrideReasonForBatch = '';
      let suppressed = 0;
      for (const file of filesList) {
        const doUpload = async () => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', isTaxSearchDoc ? 'tax_search_document' : 'other');
          if (isTaxSearchDoc) {
            formData.append('is_tax_search_document', 'true');
          }
          if (overrideReasonForBatch) {
            formData.append('override_reason', overrideReasonForBatch);
          }
          return axios.post(`${API}/orders/${orderData.id}/files/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        };
        try {
          const res = await doUpload();
          if (res?.data?.duplicate_suppressed) suppressed += 1;
        } catch (err) {
          if (is423OverrideRequired(err) && (isAdmin || isTeamLeader)) {
            const reason = await promptForOverrideReason(
              'Order is locked — enter override reason to upload',
              `This order is locked (${err.response.data.detail.order_status}). Override reason will be audited.`,
            );
            if (!reason) {
              toast.info('Upload cancelled');
              return;
            }
            overrideReasonForBatch = reason;
            const res = await doUpload();
            if (res?.data?.duplicate_suppressed) suppressed += 1;
          } else {
            throw err;
          }
        }

        // Log activity for file upload
        await logFileActivity('upload', { 
          filename: file.name, 
          size: file.size,
          category: isTaxSearchDoc ? 'Tax Search Document' : 'Other'
        });
      }
      
      uploadOutcomeToast(filesList, suppressed, '');
      await fetchFiles(true);
      window.dispatchEvent(new CustomEvent('filesChangedInOrder'));
    } catch (error) {
      console.error('Error uploading files:', error);

      // DUP-SUBMIT-001: refresh first — a lost response may hide a successful upload.
      const landed = await filesAlreadyUploaded(filesList);
      if (landed.length === filesList.length) {
        toast.info('Upload already recorded — file list refreshed, no retry needed', { id: 'dup-upload' });
        window.dispatchEvent(new CustomEvent('filesChangedInOrder'));
        return;
      }
      
      // Show specific error message from backend
      let errorMessage = 'Failed to upload files';
      if (error.response?.status === 423 && error.response?.data?.detail?.message) {
        errorMessage = error.response.data.detail.message;
      } else if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : (error.response.data.detail.message || 'Failed to upload files');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = `Upload error: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploadingFiles(false);
    }
  };

  // Server-side endpoints already emit activity events for file operations.
  // The old POST /orders/{id}/activity always returned 405 (route is GET-only)
  // and only produced console noise — kept as a no-op for call-site stability.
  const logFileActivity = async () => {};

  // Jun 2026 — Option C helpers.
  // ─────────────────────────────────────────────────────────────
  // `promptForOverrideReason(title, description)`
  //   Opens the inline override-reason modal and resolves with the
  //   trimmed reason (≥10 chars) when the user confirms, or null on
  //   cancel. Single-use Promise — closing the modal always resolves.
  // `is423OverrideRequired(err)`
  //   True only for HTTP 423 responses where the backend explicitly
  //   signaled `requires_override_reason: true`. Any other 423
  //   (no-capability lock) bubbles up as a normal error so the caller
  //   shows the standard toast.
  const promptForOverrideReason = (title, description) => new Promise((resolve) => {
    setOverrideModal({
      open: true,
      title,
      description,
      pending: (val) => {
        setOverrideModal({ open: false, title: '', description: '', pending: null });
        resolve(val);
      },
    });
  });
  const is423OverrideRequired = (error) => {
    const d = error?.response?.data?.detail;
    return error?.response?.status === 423
      && typeof d === 'object'
      && d?.requires_override_reason === true;
  };

  const handleSendFile = async (recipients, message) => {
    try {
      await axios.post(`${API}/orders/${orderData.id}/files/${selectedFile.id}/send`, {
        recipients,
        message
      });
      
      await logFileActivity('send', { 
        filename: selectedFile.filename, 
        recipients: recipients.join(', '),
        message 
      });
      
      toast.success('File sent successfully');
      setShowSendModal(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error sending file:', error);
      toast.error('Failed to send file');
    }
  };

  const handleRenameFile = async () => {
    try {
      const doRename = (overrideReason) =>
        axios.patch(`${API}/orders/${orderData.id}/files/${selectedFile.id}`, {
          filename: newFilename,
          ...(overrideReason ? { override_reason: overrideReason } : {}),
        });
      try {
        await doRename();
      } catch (err) {
        if (is423OverrideRequired(err) && (isAdmin || isTeamLeader)) {
          const reason = await promptForOverrideReason(
            'Order is locked — enter override reason to rename',
            `This order is locked (${err.response.data.detail.order_status}). Override reason will be audited.`,
          );
          if (!reason) {
            toast.info('Rename cancelled');
            return;
          }
          await doRename(reason);
        } else {
          throw err;
        }
      }

      await logFileActivity('rename', { 
        old_filename: selectedFile.filename, 
        new_filename: newFilename 
      });
      
      toast.success('File renamed successfully');
      setShowRenameModal(false);
      setSelectedFile(null);
      setNewFilename('');
      await fetchFiles(true);
    } catch (error) {
      console.error('Error renaming file:', error);
      // Surface the locked-message when applicable.
      let errorMessage = 'Failed to rename file';
      if (error.response?.status === 423 && error.response?.data?.detail?.message) {
        errorMessage = error.response.data.detail.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleDeleteFile = async () => {
    try {
      const deletedFileId = selectedFile.id;
      await axios.delete(`${API}/orders/${orderData.id}/files/${deletedFileId}`);
      
      await logFileActivity('delete', { filename: selectedFile.filename });
      
      // Notify JurisdictionManager to remove this file from source_evidence state
      window.dispatchEvent(new CustomEvent('fileDeletedFromFilesTab', { detail: { fileId: deletedFileId } }));
      
      toast.success('File deleted successfully');
      setShowDeleteModal(false);
      setSelectedFile(null);
      // FILES-FLICKER-001: remove the row locally (instant count/limit
      // update, scroll preserved) then reconcile silently in background —
      // no loading skeleton, no full-list flash.
      setFiles(prev => prev.filter(f => f.id !== deletedFileId));
      fetchFiles(true);
      window.dispatchEvent(new CustomEvent('filesChangedInOrder'));
    } catch (error) {
      console.error('Error deleting file:', error);
      // Surface meaningful server-provided reason instead of a generic message.
      // Order of precedence: backend `detail` → backend `message` → HTTP status
      // fallback → generic. This prevents the "Failed to delete file" toast
      // from masking 403 (permission) or 404 (not found) responses.
      const status = error?.response?.status;
      const backendDetail =
        error?.response?.data?.detail ||
        error?.response?.data?.message;
      let msg;
      if (backendDetail) {
        msg = backendDetail;
      } else if (status === 403) {
        msg = 'You do not have permission to delete this file.';
      } else if (status === 404) {
        msg = 'File not found. It may have already been deleted.';
      } else if (status === 401) {
        msg = 'Your session has expired. Please log in again.';
      } else {
        msg = 'Failed to delete file. Please try again or contact support.';
      }
      toast.error(msg);
    }
  };

  // QC-PORTAL-HANDOFF-001 — files whose portal upload was confirmed at completion
  const portalHandoffIds = new Set((orderData?.qc_portal_handoff?.documents || []).map((d) => d.file_id));


  const handleDownload = async (file) => {
    try {
      const token = localStorage.getItem('token');
      
      // Direct browser download — no blob, no .tmp files, no Save As popup
      // Build a URL the browser can navigate to directly with the token as a query param
      const downloadUrl = `${API}/orders/${orderData.id}/files/${file.id}/direct-download?token=${encodeURIComponent(token)}`;
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', file.filename || file.file_name || 'document.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      await logFileActivity('download', { filename: file.filename || file.file_name });
      // Jun 14, 2026 — FILES-TOAST-001: removed success toast.
      // The browser already shows its native download indicator —
      // a duplicate "Downloaded …" toast is just noise.
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handlePreviewFile = async (file) => {
    try {
      // Check if order is completed before allowing preview
      if (orderData?.status !== 'complete') {
        toast.error('Document preview is only available for completed orders');
        return;
      }

      // Jun 14, 2026 — FILES-TOAST-001: removed the redundant
      // "Loading file for preview..." toast.info — the click target
      // is already a clear user gesture and the new tab opens
      // shortly after. Errors below still surface their own toasts.

      // Fetch file content with authentication headers
      const response = await axios.get(`${API}/orders/${orderData.id}/files/${file.id}/preview`, {
        responseType: 'blob'
      });

      // Create blob URL for preview
      const blob = new Blob([response.data], { type: file.content_type || 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Open preview in new tab
      const newTab = window.open(blobUrl, '_blank');
      
      if (!newTab) {
        toast.error('Please allow popups to preview documents');
        window.URL.revokeObjectURL(blobUrl);
        return;
      }

      // Clean up blob URL after a delay (tab should have loaded by then)
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 10000);

      // Log the preview activity
      await logFileActivity('preview', { filename: file.filename });
      // Jun 14, 2026 — FILES-TOAST-001: removed success toast; the
      // new tab opening is its own confirmation.
    } catch (error) {
      console.error('Error previewing file:', error);
      if (error.response?.status === 403) {
        toast.error('Document preview is only available for completed orders');
      } else if (error.response?.status === 404) {
        toast.error('File not found or has been deleted');
      } else {
        toast.error('Failed to preview file. Please try again.');
      }
    }
  };

  const FileActionMenu = ({ file, onClose }) => (
    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
      <button
        onClick={() => {
          setSelectedFile(file);
          setShowSendModal(true);
          onClose();
        }}
        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
      >
        <Mail className="w-4 h-4" />
        Send to Customer
      </button>
      <button
        onClick={() => {
          setSelectedFile(file);
          setNewFilename(file.filename);
          setShowRenameModal(true);
          onClose();
        }}
        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
      >
        <Edit className="w-4 h-4" />
        Rename
      </button>
      {/* Preview - Only for completed orders */}
      {orderData?.status === 'complete' && (
        <button
          onClick={() => {
            handlePreviewFile(file);
            onClose();
          }}
          className="w-full px-4 py-2 text-left text-sm text-purple-700 hover:bg-purple-50 flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          View File
        </button>
      )}
      <button
        onClick={() => handleDownload(file)}
        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Download
      </button>
      <button
        onClick={() => {
          setSelectedFile(file);
          setShowDeleteModal(true);
          onClose();
        }}
        className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
      >
        <Trash2 className="w-4 h-4" />
        Delete
      </button>
    </div>
  );

  const FileTile = ({ file, showActions = true }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div 
        className="relative bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-1 group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {getStatusRibbon(file)}
        
        <div className="flex flex-col items-center text-center">
          {/* File Icon */}
          <div className="mb-3">{getFileIcon(file.file_type, file.filename)}</div>
          
          {/* Filename */}
          <h3 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 break-words">
            {file.filename}
          </h3>
          
          {/* Relative Time */}
          <p className="text-xs text-gray-500 mb-3">
            {getRelativeTime(file.uploaded_at || file.created_at)}
          </p>
          
          {/* Action Icons (on hover) */}
          {showActions && (
            <div className={`flex gap-2 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
              {/* Send - Always available */}
              {canSendFiles && (
                <button
                  onClick={() => {
                    setSelectedFile(file);
                    setShowSendModal(true);
                  }}
                  className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                  title="Send"
                >
                  <Mail className="w-4 h-4" />
                </button>
              )}
              
              {/* Rename - Only if not locked or is Team Lead */}
              {canRenameFiles && (
                <button
                  onClick={() => {
                    setSelectedFile(file);
                    setNewFilename(file.filename);
                    setShowRenameModal(true);
                  }}
                  className="p-1.5 rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
                  title="Rename"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
              
              {/* View/Preview - Only for completed orders, all file types */}
              {orderData?.status === 'complete' && (
                <button
                  onClick={() => handlePreviewFile(file)}
                  className="p-1.5 rounded-full bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                  title="View File"
                >
                  <Eye className="w-4 h-4" />
                </button>
              )}
              
              {/* Download - Always available */}
              <button
                onClick={() => handleDownload(file)}
                className="p-1.5 rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
              
              {/* Delete - Only if not locked or is Team Lead, and not final PDF */}
              {canDeleteFiles && !file.is_final_pdf && (
                <button
                  onClick={() => {
                    setSelectedFile(file);
                    setShowDeleteModal(true);
                  }}
                  className="p-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              
              {/* Show lock icon for final PDFs */}
              {file.is_final_pdf && (
                <div
                  className="p-1.5 rounded-full bg-yellow-50 text-yellow-600"
                  title="Final PDF - Protected"
                >
                  <Lock className="w-4 h-4" />
                </div>
              )}
            </div>
          )}
          
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <FileActionMenu file={file} onClose={() => setShowMenu(false)} />
            )}
          </div>
        </div>
      </div>
    );
  };

  const UploadTile = () => {
    const isDisabled = !canUploadFiles || isCompleted;
    
    return (
      <div
        className={`relative bg-white rounded-xl border-2 border-dashed p-4 transition-all duration-200 ${
          isDisabled 
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' 
            : `hover:shadow-md hover:-translate-y-1 cursor-pointer ${
                dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
              }`
        }`}
        onClick={isDisabled ? undefined : () => fileInputRef.current?.click()}
        onDragEnter={isDisabled ? undefined : handleDragEnter}
        onDragLeave={isDisabled ? undefined : handleDragLeave}
        onDragOver={isDisabled ? undefined : handleDragOver}
        onDrop={isDisabled ? undefined : handleDrop}
      >
      <div className="flex flex-col items-center text-center py-8">
        <div className="w-16 h-20 bg-gradient-to-b from-blue-50 to-blue-100 rounded-lg border border-blue-200 flex flex-col items-center justify-center shadow-sm mb-3">
          <Upload className="w-8 h-8 text-blue-600 mb-1" />
          <span className="text-xs font-medium text-blue-700 uppercase">Upload</span>
        </div>
        
        <h3 className="text-sm font-medium text-gray-900 mb-1">
          {isDisabled ? 'Upload Disabled' : 'Upload File'}
        </h3>
        
        <p className="text-xs text-gray-500 mb-2">
          {isDisabled 
            ? 'Order is locked' 
            : (uploadingFiles ? 'Uploading...' : 'Drag & drop or click')
          }
        </p>
        
        {!isDisabled && (
          <p className="text-xs text-gray-400">
            PDF, PNG, JPG up to 10MB
          </p>
        )}
        
        {isDisabled && isProcessor && (
          <p className="text-xs text-gray-400">
            Team Lead approval required
          </p>
        )}
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        accept=".pdf,.doc,.docx,.nb,.one,.rtf,.jpg,.jpeg,.png,.tif,.tiff,.gif,.mp3,.m4a,.wav,.aac,.amr,.xls,.xlsx"
        onChange={(e) => handleFileUpload(Array.from(e.target.files), false)}
        disabled={isDisabled}
      />
    </div>
    );
  };

  // FILES-ERR-001: 401/403 → the real authorization error; 5xx/network → "Unable to load files" + Retry.
  if (!loading && fetchError) {
    const isAuth = fetchError.kind === 'auth';
    return (
      <div className="od-wide py-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }} data-testid="files-fetch-error" data-error-kind={fetchError.kind}>
          <div className={`mx-auto mb-4 h-14 w-14 rounded-full flex items-center justify-center ${isAuth ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
            <AlertCircle className="h-7 w-7" />
          </div>
          <h3 className="text-base font-semibold text-slate-900" data-testid="files-fetch-error-title">{isAuth ? 'Files access restricted' : 'Unable to load files'}</h3>
          <p className="mt-1 text-sm text-slate-600" data-testid="files-fetch-error-detail">{fetchError.message}{fetchError.status ? ` (HTTP ${fetchError.status})` : ''}</p>
          {!isAuth && (
            <button type="button" data-testid="files-fetch-retry" onClick={() => fetchFiles()} className="mt-5 inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }


  if (loading) {
    return (
      <div className="od-wide py-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-6" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="flex gap-4 mb-6">
              <div className="h-10 bg-gray-200 rounded w-24"></div>
              <div className="h-10 bg-gray-200 rounded w-32"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-gray-100 rounded-xl p-4 h-48"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is a Tax Search order
  const isTaxSearchOrder = orderData?.client_product === 'Tax Search' || 
                           orderData?.client_product === 'Update Tax Search';
  
  // Find all Tax Search Documents (can be multiple files)
  // Include: is_tax_search_document OR is_final_pdf OR tax_certificate_package
  const taxSearchDocuments = files.filter(file => 
    file.is_tax_search_document === true ||
    file.is_final_pdf === true ||
    file.file_category === 'tax_certificate_package'
  );
  
  // Find clarification response files
  const clarificationFiles = files.filter(file => 
    file.file_type === 'clarification_response' || 
    file.file_category === 'clarification_response'
  );
  
  // Filter out special document types from the regular file list to avoid duplication
  // Special types: tax search documents, MLC letters, clarification responses, mail-away responses, final PDFs
  const regularFiles = files.filter(f => 
    !f.is_tax_search_document && 
    !f.is_final_pdf &&
    f.file_category !== 'tax_certificate_package' &&
    f.file_type !== 'clarification_response' &&
    f.file_category !== 'clarification_response' &&
    f.file_type !== 'mlc_letter' &&
    f.file_type !== 'mail_away_response'
  );

  return (
    <div className="od-wide py-4">
      {/* Accounting Read-Only Banner — view & download allowed; mutations blocked. */}
      {isAccounting && (
        <div
          className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-start gap-3 mb-4"
          data-testid="accounting-files-readonly-banner"
        >
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-indigo-800">Read-Only View — Accounting</h3>
            <p className="text-sm text-indigo-700 mt-1">
              You can view and download every file. Uploading, deleting, and renaming
              are disabled for the Accounting role.
            </p>
          </div>
        </div>
      )}
      {/* Jun 2026 — Option C locked-order banner. Surfaces the override
          flow for Admin / Team Leaders. Processors & QC see the existing
          static "Order is locked" copy on the dropzone (they have no
          override capability), so the banner is hidden for them. Deletion
          is always Admin-only on locked orders — the banner says so to
          set correct expectations. */}
      {isCompleted && canPostCompletionUpload && (
        <div className="pc-banner mb-4" data-testid="files-post-completion-banner">
          <span className="pc-icon-tile flex-shrink-0" style={{ width: 40, height: 40 }}><Lock className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /></span>
          <p className="text-sm text-slate-600 leading-relaxed pt-0.5">
            <span className="font-semibold text-slate-900">Order is completed and locked.</span> You can <span className="font-semibold text-slate-800">Add Document</span> for reference — a reason is required and every upload is audited. Added files are never delivered or resent to the client, and the order stays Completed.
          </p>
        </div>
      )}
      {isOrderLocked && !isCompleted && (isAdmin || isTeamLeader) && !isAccounting && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 mb-4"
          data-testid="files-locked-override-banner"
        >
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-900">Order is locked</h3>
            <p className="text-sm text-amber-800 mt-1">
              You can still <strong>upload</strong> and <strong>rename</strong> files —
              you'll be asked for an override reason (min 10 chars) which is audited.
              <span className="block mt-1 text-xs">
                File <strong>deletion</strong> is Admin-only on locked orders; unlock
                via the order header or escalate to Admin.
              </span>
            </p>
          </div>
        </div>
      )}
      <div className="pc-card p-5">
        
        {/* Header with Upload Button */}
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-4 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <span className="pc-icon-tile flex-shrink-0" style={{ width: 44, height: 44 }}><FileText className="w-5 h-5" /></span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900 leading-tight">Your Files</h2>
              <p className="text-[13px] text-slate-500 mt-0.5" data-testid="files-count">
                {files.length} file{files.length !== 1 ? 's' : ''} uploaded
              </p>
            </div>
          </div>
          {canUploadFiles && (
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="pc-grad-btn h-10 px-4 inline-flex items-center gap-2 text-sm flex-shrink-0"
              data-testid="open-upload-modal-btn"
              data-post-completion={isCompleted ? 'true' : 'false'}
            >
              <Plus className="w-4 h-4" />
              {isCompleted ? 'Add Document' : 'Upload Files'}
            </button>
          )}
        </div>

        {/* COMPLETED-ORDER-DOC-UPLOAD: premium reference-only modal (same handler, same validation) */}
        {isCompleted && canPostCompletionUpload ? (
          <PostCompletionUploadModal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            onUpload={handleCategoryUpload}
            uploading={uploadingFiles}
          />
        ) : (
        <FileUploadModal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadModalCategory(''); // Reset category when closing
          }}
          onUpload={handleCategoryUpload}
          orderStatus={orderData?.status}
          isDualMlsOrder={isDualMlsOrder}
          userRole={!isOrderDesk && user?.role === 'order_desk' ? 'search_processor' : user?.role} // OD-DUAL-ROLE-001: OD + held Search Processor uploads as a processor on their own order
          clientProduct={orderData?.client_product}
          uploading={uploadingFiles}
          existingFileCount={files.filter(f => f.is_active !== false && (f.uploaded_by_role === 'search_processor' || f.uploaded_by === user?.full_name)).length}
          supportingDocCount={files.filter(f => f.is_active !== false && f.file_category === 'supporting_document').length}
          initialCategory={uploadModalCategory}
        />
        )}

        {/* Tax Search Document Section - REMOVED: Duplicate display issue
            The generated Tax Cert PDF is now shown in the "Tax Search Documents" 
            section within FileCategorySection component, which has proper file actions.
            Keeping this section caused duplicate display of the same file.
        */}
        
        {/* Mail-Away Documents Section - Show for Mail-Away orders or orders that were Mail-Away */}
        {(orderData?.status === 'mail_away' || orderData?.mailaway_details || files.some(f => f.file_type === 'mlc_letter' || f.file_type === 'mailaway_response')) && (
          <div className="mb-6 space-y-6">
            {/* MLC Letter Subsection */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(15,23,42,0.04)' }}>
              {/* Section Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 rounded-t-xl shadow-md">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-base font-bold text-white">MLC Letter</h3>
                </div>
              </div>
              
              {/* Section Content */}
              <div className="p-6">
                {(() => {
                  // Find MLC Letter files - use file_type as single source of truth
                  const mlcLetters = files.filter(file => file.file_type === 'mlc_letter');
                  
                  return mlcLetters.length > 0 ? (
                    <div className="space-y-3">
                      {mlcLetters.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-4 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg hover:shadow-md transition-shadow"
                        >
                          {/* PDF Icon */}
                          <div className="w-10 h-12 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-lg flex items-center justify-center border border-indigo-300 flex-shrink-0">
                            <FileText className="w-5 h-5 text-indigo-700" />
                          </div>
                          
                          {/* File Details */}
                          <div className="flex-1 min-w-0">
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-indigo-900 hover:text-indigo-700 hover:underline truncate block"
                            >
                              {file.display_name || file.filename}
                            </a>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-indigo-600 font-medium">
                                {(file.file_size / 1024).toFixed(1)} KB
                              </span>
                              <span className="text-xs text-slate-500">
                                {formatDateOnlyET(file.uploaded_at || file.created_at)}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Generated
                              </span>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            {/* Download Button */}
                            <button
                              onClick={() => handleDownload(file)}
                              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                              data-testid="download-mlc-btn"
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-slate-600 font-medium">No MLC Letter has been generated yet</p>
                      <p className="text-sm text-slate-500 mt-1">
                        MLC Letter will be automatically generated when you create a mail-away request
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
            
            {/* Mail-Away Response Subsection */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(15,23,42,0.04)' }}>
              {/* Section Header */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 rounded-t-xl shadow-md">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-base font-bold text-white">Mail-Away Response(s)</h3>
                </div>
              </div>
              
              {/* Section Content */}
              <div className="p-6">
                {(() => {
                  // Find Mail-Away Response files - use file_type as single source of truth
                  const responseFiles = files.filter(file => file.file_type === 'mail_away_response');
                  
                  return responseFiles.length > 0 ? (
                    <div className="space-y-3">
                      {responseFiles.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg hover:shadow-md transition-shadow"
                        >
                          {/* PDF Icon */}
                          <div className="w-10 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center border border-green-300 flex-shrink-0">
                            <FileText className="w-5 h-5 text-green-700" />
                          </div>
                          
                          {/* File Details */}
                          <div className="flex-1 min-w-0">
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-green-900 hover:text-green-700 hover:underline truncate block"
                            >
                              {file.display_name || file.filename}
                            </a>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-green-600 font-medium">
                                {(file.file_size / 1024).toFixed(1)} KB
                              </span>
                              <span className="text-xs text-slate-500">
                                Uploaded: {formatDateOnlyET(file.uploaded_at || file.created_at)}
                              </span>
                              {file.uploaded_by_name && (
                                <span className="text-xs text-slate-500">
                                  by {file.uploaded_by_name}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            {/* Download Button */}
                            <button
                              onClick={() => handleDownload(file)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-slate-600 font-medium">No response has been received yet</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Response files will appear here when Team Leader or Admin marks response as received
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Clarification Documents Section - For orders in clarification or clarification_received status */}
        {(orderData?.status === 'clarification' || orderData?.status === 'clarification_received' || clarificationFiles.length > 0) && (
          <div className="mb-6">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(15,23,42,0.04)' }}>
              {/* Section Header */}
              <div className="bg-gradient-to-r from-orange-600 to-amber-600 px-6 py-4 rounded-t-xl shadow-md">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-base font-bold text-white">Clarification Documents</h3>
                </div>
              </div>
              
              {/* Section Content */}
              <div className="p-6">
                {(() => {
                  // Find Clarification Response files
                  const clarificationFiles = files.filter(file => 
                    file.file_type === 'clarification_response' || 
                    file.file_category === 'clarification_response'
                  );
                  
                  return clarificationFiles.length > 0 ? (
                    <div className="space-y-3">
                      {/* Show "received with note only" banner if status is received but no files */}
                      {(orderData?.status === 'clarification_received') && clarificationFiles.length === 0 && (
                        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-green-800">Clarification response received</p>
                            <p className="text-xs text-green-600 mt-0.5">No documents attached — response was provided via note only.</p>
                          </div>
                        </div>
                      )}
                      {clarificationFiles.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg hover:shadow-md transition-shadow"
                        >
                          {/* PDF Icon */}
                          <div className="w-10 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg flex items-center justify-center border border-orange-300 flex-shrink-0">
                            <FileText className="w-5 h-5 text-orange-700" />
                          </div>
                          
                          {/* File Details */}
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() => handleDownload(file)}
                              className="text-sm font-semibold text-orange-900 hover:text-orange-700 hover:underline truncate block text-left"
                            >
                              {file.display_name || file.file_name || file.filename}
                            </button>
                            <div className="flex items-center gap-3 mt-1">
                              {file.file_size && (
                                <span className="text-xs text-orange-600 font-medium">
                                  {(file.file_size / 1024).toFixed(1)} KB
                                </span>
                              )}
                              {file.uploaded_at && (
                                <span className="text-xs text-slate-500">
                                  {formatDateOnlyET(file.uploaded_at)}
                                </span>
                              )}
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Response Received
                              </span>
                            </div>
                          </div>
                          
                          {/* Delete Button */}
                          {!isOrderLocked && canUploadFiles && (
                            <button
                              onClick={() => {
                                setSelectedFile(file);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete file"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : orderData?.status === 'clarification_received' ? (
                    // Received but no files — note-only clarification
                    <div className="text-center py-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-green-50 rounded-full">
                        <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="text-slate-600 font-medium">Clarification response received</p>
                      <p className="text-sm text-slate-500 mt-1">
                        No documents attached — response was provided via note only.
                      </p>
                    </div>
                  ) : (
                    // Pending — still awaiting clarification
                    <div className="text-center py-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-orange-50 rounded-full">
                        <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-slate-600 font-medium">Awaiting clarification response</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Response files will appear here when clarification is marked as received
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Locked Order Banner for Processors */}
        {isOrderLocked && isProcessor && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-900 text-sm">Order Completed</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  This order has been completed and locked. File deletion and editing are disabled. 
                  Team Lead approval is required to make changes.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* MLS-DOC-CLASS-001: source filter chips (dual MLS + Tax orders only) */}
        {isDualMlsOrder && (
          <div className="mb-3 flex items-center gap-1.5" data-testid="file-class-filter">
            {[['all', 'All', classifiableFiles.length], ['mls', 'MLS', classifiableFiles.filter(isMlsFile).length], ['tax', 'Tax', classifiableFiles.filter((f) => !isMlsFile(f)).length]].map(([k, label, n]) => (
              <button key={k} type="button" onClick={() => setClassFilter(k)} data-testid={`file-class-filter-${k}`}
                className={`h-7 rounded-full border px-3 text-xs font-semibold transition-colors ${classFilter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                {label} ({n})
              </button>
            ))}
          </div>
        )}
        {/* Files Grouped by Category */}
        <div className="mb-6">
          <FileCategorySections
            files={(isDualMlsOrder && classFilter !== 'all' ? files.filter((f) => (classFilter === 'mls') === isMlsFile(f)) : files)
              .map((f) => (portalHandoffIds.has(f.id) ? { ...f, portal_handoff: orderData.qc_portal_handoff } : f))}
            showClassification={isDualMlsOrder}
            onDownload={handleDownload}
            onPreview={(file) => openAuthenticatedFile(file.url, file.filename)}
            onRename={(file) => {
              setSelectedFile(file);
              setNewFilename(file.filename);
              setShowRenameModal(true);
            }}
            onDelete={(file) => {
              setSelectedFile(file);
              setShowDeleteModal(true);
            }}
            onSend={(file) => {
              setSelectedFile(file);
              setShowSendModal(true);
            }}
            canEdit={canRenameFiles}
            canDelete={canDeleteFiles}
            canSend={canSendFiles}
            formatFileSize={formatFileSize}
            getRelativeTime={getRelativeTime}
          />
        </div>

        {/* Send Modal */}
        {showSendModal && selectedFile && (
          <SendFileModal
            file={selectedFile}
            onSend={handleSendFile}
            onClose={() => {
              setShowSendModal(false);
              setSelectedFile(null);
            }}
          />
        )}

        {/* Rename Modal */}
        {showRenameModal && selectedFile && (
          <RenameFileModal
            filename={newFilename}
            onFilenameChange={setNewFilename}
            onRename={handleRenameFile}
            onClose={() => {
              setShowRenameModal(false);
              setSelectedFile(null);
              setNewFilename('');
            }}
          />
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedFile && (
          <DeleteFileModal
            file={selectedFile}
            onDelete={handleDeleteFile}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedFile(null);
            }}
          />
        )}

        {/* Jun 2026 — Option C override-reason modal. Reused for both
            upload (single instance survives a multi-file batch via the
            `overrideReasonForBatch` closure) and rename. The textarea
            value lives in local state; on confirm/cancel the pending
            promise resolves with the reason or null. */}
        {overrideModal.open && (
          <OverrideReasonModal
            title={overrideModal.title}
            description={overrideModal.description}
            onCancel={() => overrideModal.pending && overrideModal.pending(null)}
            onConfirm={(reason) => overrideModal.pending && overrideModal.pending(reason)}
          />
        )}
      </div>
    </div>
  );
};

// Inline override-reason modal — kept inside the file to avoid an extra
// shared component for a single use-case. Mirrors the look-and-feel of
// the existing FileModals.jsx components.
const OverrideReasonModal = ({ title, description, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('');
  const trimmed = (reason || '').trim();
  const isValid = trimmed.length >= 10;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="files-override-reason-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-3">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            {description && <p className="text-sm text-slate-600 mt-1">{description}</p>}
          </div>
        </div>
        <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="files-override-reason-input">
          Override reason <span className="text-rose-500">*</span>
          <span className="text-xs text-slate-500 ml-1">(min 10 characters)</span>
        </label>
        <textarea
          id="files-override-reason-input"
          data-testid="files-override-reason-input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          placeholder="e.g., Customer sent revised portal screenshot after completion; replacing previous upload."
          autoFocus
        />
        <p className="text-[11px] text-slate-500 mt-1">
          {trimmed.length}/10 characters — the reason will be recorded in the order's activity log.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} data-testid="files-override-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(trimmed)}
            disabled={!isValid}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="files-override-confirm-btn"
          >
            Submit override
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilesTab;
